import React, { useState, useEffect, useRef } from "react"
import { TABLE_COLUMNS, COLORS } from "../constants"
import {
  formatPosition,
  isRankingActive,
  generateRandomPercentage,
} from "../utils"
import SkeletonCard from "./SkeletonCard"
import LiveRankingModal from "../../../components/Common/Modal/LiveRankingModal"
import { FaAngleDoubleUp, FaAngleDoubleDown, FaEquals } from "react-icons/fa"
import { AnimatePresence, motion } from "framer-motion"
import { api } from "../../../utils/variables"
import axios from "axios"
import Apaexlinecolumn from "../../../additional_components/common/charts-graphs/ApexLineChart"
import { Button, Card, CardBody, Col, Row, Spinner, Tooltip } from "reactstrap"
import { Info } from "react-feather"
import { CountryFlag } from "./FilterControls"
import { Search, Inbox, Plus, Minus } from "lucide-react"
import Skeleton from "react-loading-skeleton"
import "react-loading-skeleton/dist/skeleton.css"
import { useAnalytics, ANALYTICS_EVENTS } from "../../../analytics"

// Sort icons component
const SortIcon = ({ sortKey, currentSort, currentDirection }) => {
  if (currentSort !== sortKey) {
    return (
      <span
        style={{
          marginLeft: "4px",
          color: "#ccc",
          fontSize: "12px",
        }}
      >
        ↕
      </span>
    )
  }

  return (
    <span
      style={{
        marginLeft: "4px",
        color: COLORS.primary,
        fontSize: "12px",
      }}
    >
      {currentDirection === "asc" ? "↑" : "↓"}
    </span>
  )
}

const ProgressBar = ({ value }) => (
  <div className="progress-bar-wrapper">
    <div className="progress-bar">
      <div className="progress-fill" style={{ width: `${value}%` }} />
    </div>
    <div className="progress-label">{value}</div>
  </div>
)

// Change display component with correct color logic
const ChangeDisplay = ({ changeData }) => {
  if (!changeData || typeof changeData !== "object") {
    return <span style={{ color: "#6B7280" }}>No Change</span>
  }

  const { text, direction } = changeData

  switch (direction) {
    case "up":
      // Positive change = improvement = GREEN
      return (
        <span
          style={{
            color: "#10B981",
            display: "flex",
            alignItems: "center",
            gap: "4px",
          }}
        >
          <FaAngleDoubleUp color="green" />
          <span>{text}</span>
        </span>
      )
    case "down":
      // Negative change = decline = RED
      return (
        <span
          style={{
            color: "#EF4444",
            display: "flex",
            alignItems: "center",
            gap: "4px",
          }}
        >
          <FaAngleDoubleDown color="red" />
          <span>{text}</span>
        </span>
      )
    case "none":
      // No change = YELLOW with =0
      return (
        <span
          style={{
            color: "#F59E0B",
            display: "flex",
            alignItems: "center",
            gap: "4px",
          }}
        >
          <FaEquals color="yellow" />
          <span>{text}</span>
        </span>
      )
    default:
      // Default case = plain text
      return <span style={{ color: "#6B7280" }}>{text}</span>
  }
}

const KeywordTrackingTable = ({
  keywordsLoading,
  keywordTrackingData,
  item_id,
  countryName,
  selectedLanguage,
  planKeywordLimit = Infinity,
  isOverLimit = false,
  onUpgradeRequired = () => { },
  onGenerateKeywords = null,
}) => {
  const { trackEvent } = useAnalytics()
  const [showLiveRankingModal, setShowLiveRankingModal] = useState(false)
  const [selectedKeyword, setSelectedKeyword] = useState("")
  const [liveRankingGames, setLiveRankingGames] = useState([])
  const [liveRankingTotalApps, setLiveRankingTotalApps] = useState(0)
  const [liveRankingLoading, setLiveRankingLoading] = useState(false)
  // Defaults to Total descending so the keywords with the most ranked apps — the
  // most significant/competitive ones — show first, rather than in raw API order.
  const [sortConfig, setSortConfig] = useState({ key: "total", direction: "desc" })
  const [hadPreviousData, setHadPreviousData] = useState(false)
  const [isLoadingTransition, setIsLoadingTransition] = useState(false)

  // Track if we previously had data to prevent flash of no data during refresh
  useEffect(() => {
    if (keywordTrackingData) {
      const keys = Object.keys(keywordTrackingData).filter(k => k !== "game_data")
      if (keys.length > 0) {
        setHadPreviousData(true)
        setIsLoadingTransition(false)
      }
    }
  }, [keywordTrackingData])

  // Track loading transitions with timeout to prevent flash
  useEffect(() => {
    if (keywordsLoading && hadPreviousData) {
      setIsLoadingTransition(true)
    } else if (!keywordsLoading && keywordTrackingData) {
      // Add a small delay to prevent flash
      const timer = setTimeout(() => {
        setIsLoadingTransition(false)
      }, 100)
      return () => clearTimeout(timer)
    }
  }, [keywordsLoading, hadPreviousData, keywordTrackingData])

  // New state for expandable rows
  const [expandedRowIndex, setExpandedRowIndex] = useState(null)
  const [expandedRowValue, setExpandedRowValue] = useState(null)
  const [graphData, setGraphData] = useState([])
  const [localGraphData, setLocalGraphData] = useState([])
  const [graphLoader, setGraphLoader] = useState(false)
  const [checkedItems, setCheckedItems] = useState([
    "Rank",
    "Revenue",
    "Downloads",
  ])
  const [selectedOption, setSelectedOption] = useState("Global")
  const [tright, settright] = useState(false)
  // Pagination
  const [currentPage, setCurrentPage] = useState(1)
  const pageSize = 20 // show 20 keywords per page

  const [searchKeyword, setSearchKeyword] = useState("")

  const handleSearchChange = (e) => {
    setSearchKeyword(e.target.value)
    setCurrentPage(1) // reset pagination on search
  }

  // Reset to page 1 when a new keyword is added so the user sees it immediately
  const prevKeywordCountRef = useRef(0)
  useEffect(() => {
    const currentCount = Object.keys(keywordTrackingData || {}).filter(k => k !== 'game_data').length
    if (currentCount > prevKeywordCountRef.current) {
      setCurrentPage(1)
    }
    prevKeywordCountRef.current = currentCount
  }, [keywordTrackingData])

  // Automatically expand the first row on initial load (only once)
  // const hasAutoExpanded = useRef(false)
  // useEffect(() => {
  //   if (hasAutoExpanded.current) return
  //   if (!keywordTrackingData || keywordsLoading) return

  //   const entries = Object.entries(keywordTrackingData || {}).filter(
  //     ([k]) => k !== "game_data"
  //   )
  //   if (entries.length === 0) return

  //   const firstKeyword = entries[0][0]

  //   // Expand the first row (globalIndex = 0)
  //   setExpandedRowIndex(0)
  //   setExpandedRowValue(firstKeyword)

  //   // Fetch its graph
  //   getGraphData(firstKeyword, item_id)
  //   hasAutoExpanded.current = true
  // }, [keywordTrackingData, keywordsLoading, item_id])

  // Date range state
  const formatDateToYYYYMMDD = date => {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, "0")
    const day = String(date.getDate()).padStart(2, "0")
    return `${year}-${month}-${day}`
  }

  const oneMonthBack = date => {
    const d = new Date(date)
    d.setMonth(d.getMonth() - 1)
    return formatDateToYYYYMMDD(d)
  }

  const [startDate, setStartDate] = useState(oneMonthBack(new Date()))
  const [endDate, setEndDate] = useState(formatDateToYYYYMMDD(new Date()))
  const [tooltipOpen, setTooltipOpen] = useState({})

  // Reset date range to defaults when a different row is expanded so dates don't
  // bleed from one keyword's graph into another
  useEffect(() => {
    if (expandedRowValue) {
      setStartDate(oneMonthBack(new Date()))
      setEndDate(formatDateToYYYYMMDD(new Date()))
    }
  }, [expandedRowValue])

  const handleLiveRankingClick = async (keyword) => {
    setSelectedKeyword(keyword)
    setShowLiveRankingModal(true)
    setLiveRankingLoading(true)
    setLiveRankingGames([])
    setLiveRankingTotalApps(0)
    try {
      const details = keywordTrackingData?.[keyword] || {}
      trackEvent(ANALYTICS_EVENTS.FEATURE.KEYWORD_TRACKING_LIVE_RANKING_VIEWED, {
        keyword,
        country: details.country || countryName,
        language: details.language || selectedLanguage || "english",
      })
      const res = await axios.post(
        `${api}/tracker/tracking/keyword_live_ranking`,
        {
          keyword,
          game_id: item_id,
          country: details.country || countryName,
          language: details.language || "english",
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          },
        }
      )
      setLiveRankingGames(res.data?.data?.games || [])
      setLiveRankingTotalApps(res.data?.data?.total_apps || 0)
    } catch (error) {
      console.log(error)
    } finally {
      setLiveRankingLoading(false)
    }
  }

  const handleCloseLiveRankingModal = () => {
    setShowLiveRankingModal(false)
    setSelectedKeyword("")
    setLiveRankingGames([])
    setLiveRankingTotalApps(0)
  }

  // Toggle expand row functionality
  // const toggleExpandRow = (index, keyword) => {
  //   setExpandedRowIndex(expandedRowIndex === index ? null : index);
  //   setExpandedRowValue(expandedRowIndex === index ? null : keyword);
  //   // Reset checkedItems when expanding a row
  //   if (expandedRowIndex !== index) {
  //     setCheckedItems(["Rank", "Revenue", "Downloads"]);
  //   }
  // };

  const toggleExpandRow = (globalIndex, keyword) => {
    if (expandedRowIndex === globalIndex) {
      setExpandedRowIndex(null)
      setExpandedRowValue(null)
    } else {
      setExpandedRowIndex(globalIndex)
      setExpandedRowValue(keyword)
      setCheckedItems(["Rank", "Revenue", "Downloads"])
      // Fetch graph only on explicit expand action
      getGraphData(keyword, item_id)

      const details = keywordTrackingData?.[keyword] || {}
      trackEvent(ANALYTICS_EVENTS.FEATURE.KEYWORD_TRACKING_KEYWORD_VIEWED, {
        keyword,
        country: details.country || countryName,
        language: details.language || selectedLanguage,
      })
    }
  }

  // Tooltip open/close state for each table header

  const toggleTooltip = key => {
    setTooltipOpen(prev => ({
      ...prev,
      [key]: !prev[key],
    }))
  }

  // Handle checkbox changes for graph data
  const handleCheckboxChange = (e, itemText) => {
    if (e.target.checked) {
      setCheckedItems(prevItems => [...prevItems, itemText])
    } else {
      setCheckedItems(prevItems => prevItems.filter(item => item !== itemText))
    }
  }

  // Get graph data for keyword
  const getGraphData = async (keyword, gameId) => {
    setGraphLoader(true)
    const data = {
      type: "keyword",
      entity_id: keyword,
      game_id: gameId,
      start_date: startDate,
      end_date: endDate,
    }
    try {
      const res = await axios.post(
        `${api}/tracker/tracking/keyword_details`,
        data,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          },
        }
      )
      setGraphData(res.data.data.rankings)
    } catch (error) {
      console.log(error)
    } finally {
      setGraphLoader(false)
      setCheckedItems(["Rank", "Revenue", "Downloads"])
    }
  }

  // Get local graph data
  const getLocalGraphData = async gameId => {
    setGraphLoader(true)
    const data = {
      type: "keyword",
      item_id: gameId,
      start_date: startDate,
      end_date: endDate,
    }
    try {
      const res = await axios.post(`${api}/tracker/tracking/app_graph`, data, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        },
      })
      setLocalGraphData(res.data.history)
    } catch (error) {
      console.log(error)
    } finally {
      setGraphLoader(false)
      setCheckedItems(["Rank", "Revenue", "Downloads"])
    }
  }

  // Handle filter change
  const handleChange = event => {
    const selected = event.target.value
    setSelectedOption(selected)
    applyFilter(selected)
  }

  // Apply filter
  const applyFilter = selected => {
    if (selected === "Local") {
      getLocalGraphData(item_id)
    } else {
      getGraphData(expandedRowValue, item_id)
    }
  }

  // Removed effect-based fetch on expand to avoid unintended refetches after data mutations

  const handleSort = key => {
    let direction = "asc"
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc"
    }
    setSortConfig({ key, direction })
  }

  const sortData = data => {
    if (!sortConfig.key) return data

    return [...data].sort((a, b) => {
      let aValue, bValue

      if (sortConfig.key === "currentPosition") {
        // Use raw numeric values for sorting
        aValue = a.currRaw
        bValue = b.currRaw
      } else if (sortConfig.key === "lastPosition") {
        // Use raw numeric values for sorting
        aValue = a.lastRaw
        bValue = b.lastRaw
      } else if (sortConfig.key === "total") {
        aValue = a.totalApps
        bValue = b.totalApps
      } else {
        return 0
      }

      if (sortConfig.key === "total") {
        // Missing totals sort as 0 (lowest), not "worst position" (MAX_SAFE_INTEGER) —
        // there's no ranking-quality semantic here, just an app count.
        if (aValue === null || aValue === undefined) aValue = 0
        if (bValue === null || bValue === undefined) bValue = 0
      } else {
        // Handle null/undefined values (treat as highest numbers - worst positions)
        // For positions, higher numbers mean worse ranking, so undefined should be treated as the worst
        if (aValue === null || aValue === undefined)
          aValue = Number.MAX_SAFE_INTEGER
        if (bValue === null || bValue === undefined)
          bValue = Number.MAX_SAFE_INTEGER
      }

      if (sortConfig.direction === "asc") {
        return aValue - bValue
      } else {
        return bValue - aValue
      }
    })
  }

  const [isSearchOpen, setIsSearchOpen] = useState(false)
  // const [searchKeyword, setSearchKeyword] = useState("")

  const renderTableHeader = () => (
    <thead>
      <tr>
        {TABLE_COLUMNS.map(column => {
          const tooltipId = `header-tooltip-${column.key}`

          if (column.key === "keyword") {
            return (
              <th key={column.key} style={{ textAlign: "left" }}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    position: "relative",
                  }}
                >
                  {!isSearchOpen && (
                    <>
                      {/* Search Icon */}
                      <Search
                        size={16}
                        color="#F6255A" // highlighted color
                        style={{ cursor: "pointer" }}
                        onClick={() => setIsSearchOpen(true)}
                      />
                      <span style={{ fontWeight: "bold", color: "#FFFFFF" }}>
                        {column.label}
                      </span>
                    </>
                  )}

                  {isSearchOpen && (
                    <input
                      type="text"
                      autoFocus
                      placeholder="Search keyword..."
                      value={searchKeyword}
                      onChange={e => {
                        setSearchKeyword(e.target.value)
                        setCurrentPage(1)
                      }}
                      onBlur={() => setIsSearchOpen(false)}
                      style={{
                        width: "180px",
                        padding: "4px 8px",
                        borderRadius: "6px",
                        border: "1px solid #3a3d4a",
                        backgroundColor: "#1e2028",
                        color: "#FFFFFF",
                        transition: "width 0.3s ease, opacity 0.3s ease",
                        outline: "none",
                      }}
                    />
                  )}
                </div>
              </th>
            )
          }

          // Default rendering for other columns
          return (
            <th
              key={column.key}
              style={{
                textAlign: column.key === "liveRanking" ? "center" : "left",
              }}
            >
              <div
                className="keysec"
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent:
                    column.key === "liveRanking" ? "center" : "flex-start",
                  cursor:
                    column.key === "currentPosition" ||
                      column.key === "lastPosition" ||
                      column.key === "total"
                      ? "pointer"
                      : "default",
                  userSelect: "none",
                  gap: "6px",
                }}
                onClick={() => {
                  if (
                    column.key === "currentPosition" ||
                    column.key === "lastPosition" ||
                    column.key === "total"
                  ) {
                    handleSort(column.key)
                  }
                }}
              >
                {column.label}
                <span
                  id={tooltipId}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    cursor: "pointer",
                  }}
                >
                  <Info size={14} color="#9CA3AF" />
                </span>
                <Tooltip
                  isOpen={tooltipOpen[column.key]}
                  target={tooltipId}
                  toggle={() => toggleTooltip(column.key)}
                  placement="top"
                >
                  {column.tooltip}
                </Tooltip>
                {(column.key === "currentPosition" ||
                  column.key === "lastPosition" ||
                  column.key === "total") && (
                    <SortIcon
                      sortKey={column.key}
                      currentSort={sortConfig.key}
                      currentDirection={sortConfig.direction}
                    />
                  )}
              </div>
            </th>
          )
        })}
      </tr>
    </thead>
  )


  const renderTableBody = () => {
    // More aggressive approach: if we ever had data, always show skeleton during any loading/transition
    const hasData = keywordTrackingData && Object.keys(keywordTrackingData).filter(k => k !== "game_data").length > 0;
    const shouldShowSkeleton = keywordsLoading || !keywordTrackingData || isLoadingTransition;
    
    if (shouldShowSkeleton) {
      return (
        <tbody>
          {[...Array(5)].map((_, index) => (
            <tr key={`skeleton-${index}`} style={{ borderBottom: "1px solid #3a3d4a" }}>
              {TABLE_COLUMNS.map((col, colIndex) => (
                <td key={colIndex} style={{ padding: "12px 8px" }}>
                  <Skeleton baseColor="#2C2D33" highlightColor="#3A3C46" height={20} />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      )
    }

    const entries = Object.entries(keywordTrackingData || {}).filter(
      ([k]) => k !== "game_data"
    )

    if (entries.length === 0) {
      // Even if we had data before, if it is completely empty now, and not loading, show start tracking
      // (This prevents the skeleton loop when no keywords are actually tracked for an app)
      
      const showAiButton = typeof onGenerateKeywords === "function"

      return (
        <tbody>
          <tr>
            <td colSpan="6">
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: "60px 20px",
                  color: "#9CA3AF",
                }}
              >
                <div
                  style={{
                    marginTop: "18px",
                    textAlign: "center",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: "10px",
                  }}
                >
                  <div
                    style={{
                      width: "40px",
                      height: "40px",
                      borderRadius: "50%",
                      backgroundColor: "#2C2D33",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Inbox size={20} color="#6B7280" />
                  </div>
                  <span
                    style={{
                      fontSize: "14px",
                      color: "#E5E7EB",
                      fontWeight: 500,
                    }}
                  >
                    Start tracking keywords
                  </span>

                  {showAiButton && (
                    <button
                      type="button"
                      onClick={onGenerateKeywords}
                      disabled={keywordsLoading || isOverLimit}
                      title={
                        isOverLimit
                          ? "Keyword limit reached. Click to upgrade your plan."
                          : "Generate AI-powered keywords"
                      }
                      style={{
                        background:
                          keywordsLoading || isOverLimit
                            ? "#4b5563"
                            : "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                        color: COLORS.white,
                        border: "none",
                        borderRadius: "6px",
                        padding: "8px 14px",
                        fontSize: "11px",
                        fontWeight: "600",
                        cursor:
                          keywordsLoading || isOverLimit ? "not-allowed" : "pointer",
                        opacity: keywordsLoading || isOverLimit ? 0.6 : 1,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "4px",
                        whiteSpace: "nowrap",
                        minWidth: "120px",
                        height: "40px",
                        boxShadow:
                          keywordsLoading || isOverLimit
                            ? "none"
                            : "0 2px 8px rgba(102, 126, 234, 0.3)",
                        transition: "all 0.2s ease",
                      }}
                    >
                      <i
                        className="fas fa-magic"
                        style={{ fontSize: "10px" }}
                      />
                      AI Keywords
                    </button>
                  )}
                </div>
              </div>
            </td>
          </tr>
        </tbody>
      )
    }

    const keywordEntries = Object.entries(keywordTrackingData || {}).filter(
      ([key]) => key !== "game_data"
    )

    // Apply plan limits
    const allowedEntries = planKeywordLimit === Infinity ? keywordEntries : keywordEntries.slice(0, planKeywordLimit)
    const excessEntries = planKeywordLimit === Infinity ? [] : keywordEntries.slice(planKeywordLimit)

    // Prepare data for sorting
    const tableData = allowedEntries.map(([keyword, details]) => {
      const loading = !!details._loading || !details.has_data

      // The backend now computes current_rank/last_position/total_apps directly
      // (only the top 20 competitor ranks are sent in `ranks`, so counting entries
      // in `ranks` here would undercount once a keyword has more than 20 apps).
      const currRaw = details.current_rank
      const lastRaw = details.last_position
      const totalApps = details.total_apps || 0

      const currentPosition = formatPosition(currRaw, totalApps)
      const lastPosition = formatPosition(lastRaw, totalApps)
      // Backend-derived: { value, direction: "up"|"down"|"none", text } — see
      // build_change_state in tracker/tracking/app/service.py.
      const changeData = details.change

      const generateStablePercentage = seed => {
        let hash = 0
        for (let i = 0; i < seed.length; i++) {
          const char = seed.charCodeAt(i)
          hash = (hash << 5) - hash + char
          hash = hash & hash
        }
        return (Math.abs(hash) % 100) + 1
      }

      return {
        keyword,
        loading,
        currentPosition,
        lastPosition,
        changeData,
        popularity: generateStablePercentage(keyword + "_popularity"),
        competitiveness: generateStablePercentage(keyword + "_competitiveness"),
        currRaw,
        lastRaw,
        totalApps
      }
    })

    // Apply search filter
    const filteredData = tableData.filter(row =>
      row.keyword.toLowerCase().includes(searchKeyword.toLowerCase())
    )

    if (filteredData.length === 0) {
      return (
        <tbody>
          <tr>
            <td colSpan="6">
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: "40px 20px",
                  color: "#9CA3AF",
                }}
              >
                <Search size={32} color="#4B5563" style={{ marginBottom: "12px" }} />
                <p style={{ fontSize: "14px", margin: 0 }}>
                  No keywords found matching "{searchKeyword}"
                </p>
              </div>
            </td>
          </tr>
        </tbody>
      );
    }

    // Sort filtered data
    const sortedData = sortData(filteredData)

    // Pagination
    const totalItems = sortedData.length
    const totalPages = Math.ceil(totalItems / pageSize)
    const startIndex = (currentPage - 1) * pageSize
    const paginatedData = sortedData.slice(startIndex, startIndex + pageSize)

    return (
      <tbody>
        {paginatedData.map((rowData, index) => {
          const globalIndex = startIndex + index // IMPORTANT
          return (
            <React.Fragment key={rowData.keyword}>
              <tr
                style={{
                  marginBottom: "8px",
                  borderBottom: "1px solid #3a3d4a",
                }}
              >
                <td
                  style={{
                    textAlign: "left",
                    padding: "12px 8px",
                    cursor: "pointer",
                    backgroundColor: "#2C2D33",
                    color: "#FFFFFF",
                  }}
                  onClick={() => toggleExpandRow(globalIndex, rowData.keyword)}
                >
                  {/* <div className="d-flex align-items-center">
                    <CountryFlag
                      countryName={countryName}
                      style={{ marginRight: "8px", flexShrink: 0 }}
                    />
                    <div className="keyword-text-cell">
                      <strong>{rowData.keyword}</strong>
                    </div>
                  </div> */}
                  <div
                    className="d-flex align-items-center"
                    style={{ gap: "8px" }}
                  >
                    <CountryFlag
                      countryName={countryName}
                      style={{ marginRight: "4px", flexShrink: 0 }}
                    />
                    <div className="keyword-text-cell">
                      <strong>{rowData.keyword}</strong>
                    </div>

                    {/* Expand / Collapse Icon */}
                    <span
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: "18px",
                        height: "18px",
                        borderRadius: "4px",
                        background: expandedRowIndex === globalIndex ? "#F6255A" : "#3A3D4A",
                        color: "#FFFFFF",
                        fontSize: "12px",
                        cursor: "pointer",
                        flexShrink: 0,
                      }}
                    >
                      {expandedRowIndex === globalIndex ? (
                        <Minus size={12} />
                      ) : (
                        <Plus size={12} />
                      )}
                    </span>
                  </div>

                  {/* {rowData.loading && (
                    <span style={{ marginLeft: 8, fontSize: 12, color: '#9CA3AF' }}>(fetching…)</span>
                  )} */}
                </td>
                <td style={{ textAlign: "left", padding: "12px 8px" }}>
                  {rowData.loading ? 'Loading...' : rowData.currentPosition}
                </td>
                <td style={{ textAlign: "left", padding: "12px 8px" }}>
                  {rowData.loading ? 'Loading...' : rowData.lastPosition}
                </td>
                <td style={{ textAlign: "left", padding: "12px 8px" }}>
                  {rowData.loading ? <span style={{ color: '#9CA3AF' }}>Loading…</span> : <ChangeDisplay changeData={rowData.changeData} />}
                </td>
                {/* <td style={{ textAlign: "left", padding: "12px 8px" }}>
                  <ProgressBar value={rowData.popularity} />
                </td>
                <td style={{ textAlign: "left", padding: "12px 8px" }}>
                  <ProgressBar value={rowData.competitiveness} />
                </td> */}
                <td style={{ textAlign: "center", padding: "12px 8px" }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: "10px", width: "100%" }}>
                    <button
                      type="button"
                      onClick={() =>
                        handleLiveRankingClick(rowData.keyword)
                      }
                      disabled={rowData.loading}
                      style={{
                        background:
                          "linear-gradient(135deg, #5A5A5A 0%, #C49598 100%)",
                        color: "#FFFFFF",
                        border: "none",
                        borderRadius: "4px",
                        padding: "4px 10px",
                        fontSize: "11px",
                        fontWeight: "bold",
                        cursor: "pointer",
                        transition: "all 0.2s ease",
                        boxShadow: "0 2px 4px rgba(128, 128, 128, 0.3)",
                        opacity: rowData.loading ? 0.7 : 1,
                        whiteSpace: "nowrap"
                      }}
                      onMouseEnter={e => {
                        e.target.style.background =
                          "linear-gradient(135deg, #4A4A4A 0%, #B08588 100%)"
                        e.target.style.transform = "translateY(-1px)"
                        e.target.style.boxShadow =
                          "0 4px 8px rgba(128, 128, 128, 0.4)"
                      }}
                      onMouseLeave={e => {
                        e.target.style.background =
                          "linear-gradient(135deg, #5A5A5A 0%, #C49598 100%)"
                        e.target.style.transform = "translateY(0)"
                        e.target.style.boxShadow =
                          "0 2px 4px rgba(128, 128, 128, 0.3)"
                      }}
                    >
                      {rowData.loading ? 'Fetching…' : 'View'}
                    </button>
                  </div>
                </td>
                <td style={{ textAlign: "center", padding: "12px 8px", color: "#FFFFFF", fontWeight: "bold" }}>
                  {rowData.loading ? '…' : rowData.totalApps}
                </td>
              </tr>
              <AnimatePresence>
                {expandedRowIndex === globalIndex && (
                  <tr key={`expanded-${index}`} className="text-align-center">
                    <td colSpan="6">
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{
                          height: 0,
                          opacity: 0,
                          transition: { duration: 0.4, ease: "easeInOut" },
                        }}
                        transition={{ duration: 0.4, ease: "easeInOut" }}
                        style={{
                          padding: "0",
                          margin: "0",
                          backgroundColor: "transparent",
                          color: "#FFFFFF",
                          border: "none",
                          borderRadius: "0",
                          overflow: "visible",
                          boxShadow: "none",
                        }}
                      >
                        <div
                          style={{
                            padding: "20px",
                            backgroundColor: "#23272b",
                            border: "1px solid #3a3d4a",
                            borderRadius: "10px",
                            margin: "18px 10px 10px 10px",
                            boxShadow: "0 8px 20px rgba(0,0,0,0.45)",
                            overflow: "hidden",
                          }}
                        >
                          <div style={{ marginBottom: "20px" }}>
                            <h6
                              style={{
                                color: "#FFFFFF",
                                marginBottom: "10px",
                                fontSize: "16px",
                                wordBreak: "break-all",
                                whiteSpace: "normal",
                                overflowWrap: "anywhere",
                              }}
                            >
                              Keyword: <strong>{expandedRowValue}</strong>
                            </h6>
                          </div>

                          <div style={{ marginBottom: "20px" }}>
                            <div className="d-flex flex-wrap justify-content-between align-items-center">
                              <div className="d-flex flex-wrap gap-4 align-items-center">
                                <label
                                  htmlFor="start-date-input"
                                  style={{
                                    color: "#FFFFFF",
                                    marginBottom: "0",
                                    fontSize: "14px",
                                  }}
                                >
                                  From
                                </label>
                                <div>
                                  <input
                                    className="form-control"
                                    type="date"
                                    defaultValue={startDate}
                                    max={new Date().toISOString().split("T")[0]}
                                    onChange={e => {
                                      setStartDate(e.target.value)
                                    }}
                                    id="start-date-input"
                                    style={{
                                      backgroundColor: "#1e2028",
                                      border: "1px solid #3a3d4a",
                                      color: "#FFFFFF",
                                      fontSize: "14px",
                                      padding: "8px 12px",
                                    }}
                                  />
                                </div>
                              </div>

                              <div className="d-flex flex-wrap gap-4 align-items-center">
                                <label
                                  htmlFor="end-date-input"
                                  style={{
                                    color: "#FFFFFF",
                                    marginBottom: "0",
                                    fontSize: "14px",
                                  }}
                                >
                                  To
                                </label>
                                <div>
                                  <input
                                    className="form-control"
                                    type="date"
                                    defaultValue={endDate}
                                    max={new Date().toISOString().split("T")[0]}
                                    onChange={e => {
                                      setEndDate(e.target.value)
                                    }}
                                    id="end-date-input"
                                    style={{
                                      backgroundColor: "#1e2028",
                                      border: "1px solid #3a3d4a",
                                      color: "#FFFFFF",
                                      fontSize: "14px",
                                      padding: "8px 12px",
                                    }}
                                  />
                                </div>
                              </div>
                              <div className="col-md-3">
                                <select
                                  className="form-control"
                                  onChange={handleChange}
                                  style={{
                                    backgroundColor: "#1e2028",
                                    border: "1px solid #3a3d4a",
                                    color: "#FFFFFF",
                                    fontSize: "14px",
                                    padding: "8px 12px",
                                  }}
                                >
                                  <option value="Global">
                                    Global Revenue/Downloads
                                  </option>
                                  <option value="Local">
                                    App Revenue/Downloads
                                  </option>
                                </select>
                              </div>
                              <Button
                                color="primary"
                                onClick={() => applyFilter(selectedOption)}
                                style={{
                                  background:
                                    "linear-gradient(135deg, #F6255A 0%, #F6255A 100%)",
                                  border: "none",
                                  borderRadius: "6px",
                                  padding: "8px 16px",
                                  fontWeight: "600",
                                  fontSize: "14px",
                                }}
                              >
                                Apply Range
                              </Button>
                            </div>
                          </div>
                          {graphLoader ? (
                            <div
                              className="d-flex justify-content-center"
                              style={{ padding: "20px" }}
                            >
                              <Spinner></Spinner>
                            </div>
                          ) : (
                            <>
                              <div
                                style={{ marginBottom: "20px" }}
                                className="d-flex justify-content-start gap-5"
                              >
                                <div className="d-flex flex-wrap justify-content-start gap-3">
                                  <label
                                    style={{
                                      color: "#FFFFFF",
                                      display: "flex",
                                      alignItems: "center",
                                      gap: "8px",
                                      fontSize: "14px",
                                    }}
                                  >
                                    <input
                                      type="checkbox"
                                      defaultChecked="true"
                                      onChange={e =>
                                        handleCheckboxChange(e, "Rank")
                                      }
                                      style={{ accentColor: "#F6255A" }}
                                    />
                                    Rank
                                  </label>
                                  <label
                                    style={{
                                      color: "#FFFFFF",
                                      display: "flex",
                                      alignItems: "center",
                                      gap: "8px",
                                      fontSize: "14px",
                                    }}
                                  >
                                    <input
                                      type="checkbox"
                                      defaultChecked="true"
                                      onChange={e =>
                                        handleCheckboxChange(e, "Revenue")
                                      }
                                      style={{ accentColor: "#F6255A" }}
                                    />
                                    Revenue
                                  </label>
                                  <label
                                    style={{
                                      color: "#FFFFFF",
                                      display: "flex",
                                      alignItems: "center",
                                      gap: "8px",
                                      fontSize: "14px",
                                    }}
                                  >
                                    <input
                                      type="checkbox"
                                      defaultChecked="true"
                                      onChange={e =>
                                        handleCheckboxChange(e, "Downloads")
                                      }
                                      style={{ accentColor: "#F6255A" }}
                                    />
                                    Downloads
                                  </label>
                                </div>
                                <Tooltip
                                  placement="right"
                                  isOpen={tright}
                                  target="TooltipRight"
                                  toggle={() => {
                                    settright(!tright)
                                  }}
                                >
                                  Click to learn more about graph trends
                                </Tooltip>
                                <Info
                                  id="TooltipRight"
                                  size={20}
                                  onClick={() => {
                                    settright(!tright)
                                  }}
                                  style={{
                                    cursor: "pointer",
                                    color: "#FFFFFF",
                                  }}
                                />
                              </div>
                              <div
                                style={{
                                  backgroundColor: "#2C2D33",
                                  padding: "20px",
                                  borderRadius: "8px",
                                }}
                              >
                                <Apaexlinecolumn
                                  data={graphData}
                                  selectedData={selectedOption}
                                  localData={localGraphData}
                                  labels={checkedItems}
                                />
                              </div>
                            </>
                          )}
                        </div>
                      </motion.div>
                    </td>
                  </tr>
                )}
              </AnimatePresence>
            </React.Fragment>
          )
        })}
      </tbody>
    )
  }
  const keywordEntries = Object.entries(keywordTrackingData || {}).filter(
    ([k]) => k !== "game_data"
  )

  // Sort all entries by the active sort config so excess rows match the main table order
  const sortedKeywordEntries = sortConfig.key
    ? [...keywordEntries].sort((a, b) => {
        const getVal = ([, details]) => {
          if (sortConfig.key === 'total') {
            return details.total_apps || 0
          }
          const raw = sortConfig.key === 'lastPosition'
            ? details.last_position
            : details.current_rank
          return raw == null ? Number.MAX_SAFE_INTEGER : raw
        }
        const diff = getVal(a) - getVal(b)
        return sortConfig.direction === 'asc' ? diff : -diff
      })
    : keywordEntries

  // Apply plan limits
  const allowedEntries = planKeywordLimit === Infinity ? sortedKeywordEntries : sortedKeywordEntries.slice(0, planKeywordLimit)
  const excessEntries = planKeywordLimit === Infinity ? [] : sortedKeywordEntries.slice(planKeywordLimit)

  const tableDataLength = keywordEntries.length
  const totalPages = Math.ceil(tableDataLength / pageSize)

  // Render excess keywords (blurred)
  const renderExcessKeywords = () => {
    if (excessEntries.length === 0) return null

    return (
      <tbody>
        {excessEntries.map(([keyword, details]) => {
          const loading = !!details._loading || !details.has_data

          const currRaw = details.current_rank
          const lastRaw = details.last_position
          const totalAppsCount = details.total_apps || 0

          const currentPosition = formatPosition(currRaw, totalAppsCount)
          const lastPosition = formatPosition(lastRaw, totalAppsCount)
          const changeData = details.change

          const rowData = {
            keyword,
            loading,
            currentPosition,
            lastPosition,
            changeData,
            totalAppsCount,
            details
          }

          return (
            <tr
              key={`excess-${keyword}`}
              style={{
                marginBottom: "8px",
                borderBottom: "1px solid #3a3d4a",
                filter: 'blur(2px)',
                opacity: 0.6,
                position: 'relative',
                cursor: 'pointer'
              }}
              onClick={onUpgradeRequired}
              title="Upgrade your plan to access this keyword"
            >
              <td
                style={{
                  textAlign: "left",
                  padding: "12px 8px",
                  backgroundColor: "#2C2D33",
                  color: "#FFFFFF",
                  position: 'relative'
                }}
              >
                <div
                  className="d-flex align-items-center"
                  style={{ gap: "8px" }}
                >
                  <CountryFlag
                    countryName={countryName}
                    style={{ marginRight: "4px", flexShrink: 0 }}
                  />
                  <div className="keyword-text-cell">
                    <strong>{keyword}</strong>
                  </div>

                  {/* Lock icon */}
                  <i
                    className="fas fa-lock"
                    style={{
                      fontSize: '12px',
                      opacity: 0.8,
                      marginLeft: '4px'
                    }}
                  />
                </div>
              </td>
              <td
                style={{
                  textAlign: "center",
                  padding: "12px 8px",
                  backgroundColor: "#2C2D33",
                  color: "#FFFFFF",
                }}
              >
                {loading ? (
                  <Skeleton circle height={20} width={40} baseColor="#2C2D33" highlightColor="#3A3C46" />
                ) : (
                  <span style={{ color: "#6B7280" }}>{currentPosition}</span>
                )}
              </td>
              <td
                style={{
                  textAlign: "center",
                  padding: "12px 8px",
                  backgroundColor: "#2C2D33",
                  color: "#FFFFFF",
                }}
              >
                {loading ? (
                  <Skeleton height={20} width={60} baseColor="#2C2D33" highlightColor="#3A3C46" />
                ) : (
                  <span style={{ color: "#6B7280" }}>{changeData.display}</span>
                )}
              </td>
              <td
                style={{
                  textAlign: "center",
                  padding: "12px 8px",
                  backgroundColor: "#2C2D33",
                  color: "#FFFFFF",
                }}
              >
                {loading ? (
                  <Skeleton height={20} width={40} baseColor="#2C2D33" highlightColor="#3A3C46" />
                ) : (
                  <span style={{ color: "#6B7280" }}>View</span>
                )}
              </td>
              <td
                style={{
                  textAlign: "center",
                  padding: "12px 8px",
                  backgroundColor: "#2C2D33",
                  color: "#FFFFFF",
                }}
              >
                {loading ? (
                  <Skeleton height={20} width={40} baseColor="#2C2D33" highlightColor="#3A3C46" />
                ) : (
                  <span style={{ color: "#6B7280" }}>{rowData.totalAppsCount}</span>
                )}
              </td>
              <td
                style={{
                  textAlign: "center",
                  padding: "12px 8px",
                  backgroundColor: "#2C2D33",
                  color: "#FFFFFF",
                }}
              >
                {loading ? (
                  <Skeleton height={20} width={60} baseColor="#2C2D33" highlightColor="#3A3C46" />
                ) : (
                  <span style={{ color: "#6B7280" }}>
                    {generateRandomPercentage(keyword)}%
                  </span>
                )}
              </td>
            </tr>
          )
        })}
      </tbody>
    )
  }

  return (
    <>
      <div className="track_table_main">
        <div className="track_table">
          <table
            className="table"
            style={{
              width: "100%",
              borderCollapse: "collapse",
            }}
          >
            {renderTableHeader()}
            {renderTableBody()}
            {renderExcessKeywords()}
          </table>
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              marginTop: "20px",
              gap: "10px",
            }}
          >
            <button
              disabled={currentPage === 1}
              onClick={() => setCurrentPage(prev => prev - 1)}
              style={{
                padding: "6px 12px",
                backgroundColor: "#3a3d4a",
                color: "#fff",
                border: "none",
                borderRadius: "4px",
                cursor: currentPage === 1 ? "not-allowed" : "pointer",
              }}
            >
              Previous
            </button>

            {[...Array(totalPages).keys()].map(num => {
              const page = num + 1
              return (
                <button
                  key={page}
                  onClick={() => setCurrentPage(page)}
                  style={{
                    padding: "6px 12px",
                    backgroundColor:
                      page === currentPage ? "#F6255A" : "#2C2D33",
                    color: "#fff",
                    border: "none",
                    borderRadius: "4px",
                    cursor: "pointer",
                  }}
                >
                  {page}
                </button>
              )
            })}

            <button
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage(prev => prev + 1)}
              style={{
                padding: "6px 12px",
                backgroundColor: "#3a3d4a",
                color: "#fff",
                border: "none",
                borderRadius: "4px",
                cursor: currentPage === totalPages ? "not-allowed" : "pointer",
              }}
            >
              Next
            </button>
          </div>
        </div>
      </div>

      <LiveRankingModal
        show={showLiveRankingModal}
        handleClose={handleCloseLiveRankingModal}
        games={liveRankingGames}
        totalApps={liveRankingTotalApps}
        loading={liveRankingLoading}
        keyword={selectedKeyword}
      />
    </>
  )
}

export default KeywordTrackingTable
